import React, { useState, useEffect } from "react"
import { Col, Row, UncontrolledAlert, Spinner } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField } from "availity-reactstrap-validation"
import { Link, useParams } from "react-router-dom"
import { AuthUser } from "API/UserAccess"
import PageLoader from "./../../../../assets/loading/pageLoad1.gif"

const LEVEL_DESCRIPTIONS = {
  "Admin": "Full system access — all modules, all permissions",
  "Company": "Company-wide management — plants, billing, finance, reports",
  "Plant": "Plant operations — chilling centers, routes, procurement, billing",
  "CC": "Chilling center operations — routes, purchases, basic billing",
  "Route": "Route management — collection centers, purchases",
  "Center": "Collection center — data entry and view only",
  "Client": "Read-only access — dashboard and reports only",
}

const LEVEL_COLORS = {
  "Admin": "#dc2626", "Company": "#6366f1", "Plant": "#0ea5e9",
  "CC": "#22c55e", "Route": "#f59e0b", "Center": "#8b5cf6", "Client": "#6c757d",
}

const CATEGORY_ICONS = {
  "Overview": "fa-tachometer-alt", "Masters": "fa-database", "Procurement": "fa-boxes",
  "Session": "fa-calendar-check", "Billing": "fa-file-invoice-dollar", "Finance": "fa-wallet",
  "System": "fa-cogs", "Reports": "fa-chart-bar", "Other": "fa-ellipsis-h"
}
const CATEGORY_COLORS = {
  "Overview": "#6366f1", "Masters": "#0ea5e9", "Procurement": "#22c55e",
  "Session": "#a855f7", "Billing": "#f59e0b", "Finance": "#ef4444",
  "System": "#8b5cf6", "Reports": "#06b6d4", "Other": "#6c757d"
}

const EditRole = () => {
  const { id } = useParams()
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pageLoad, setPageLoad] = useState(true)
  const [role, setRole] = useState(null)
  const [roleAccess, setRoleAccess] = useState([])
  const [roleSets, setRoleSets] = useState([])
  const [roleFilters, setRoleFilters] = useState({})
  const [all, setAll] = useState(false)
  const [roleid, setRoleid] = useState("")
  const [selectedLevel, setSelectedLevel] = useState("")

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    try {
      const [accessResp, roleResp] = await Promise.all([
        api.get("accesser"), api.get("role/" + id)
      ])
      setRoleAccess(Array.isArray(accessResp.data.data) ? accessResp.data.data : accessResp.data.data?.data || [])
      const roleData = roleResp.data.data?.[0] || roleResp.data.data
      setRole(roleData)
      const accessLevelName = roleData.access_level // e.g. "Admin"

      // Find the base access level by name to get its numeric ID
      const accessList = Array.isArray(accessResp.data.data) ? accessResp.data.data : accessResp.data.data?.data || []
      const found = accessList.find(a => String(a.access_level) === String(accessLevelName))
      const numericId = found ? found.id : accessLevelName // fallback to raw value
      setRoleid(numericId)
      setSelectedLevel(found ? found.access_level : accessLevelName)

      // Load modules using the numeric base level ID
      const settingsResp = await api.get("role-settings/" + numericId)
      setRoleSets(Array.isArray(settingsResp.data.data) ? settingsResp.data.data : settingsResp.data.data?.data || [])

      // Use saved role_settings, falling back to defaults
      let savedSettings = {}
      try { savedSettings = JSON.parse(roleData.role_settings || "{}") } catch (e) { savedSettings = {} }

      // Merge with defaults: ensure all module IDs exist
      const defaultSettings = JSON.parse(settingsResp.data.settings || "{}")
      const merged = { ...defaultSettings }
      Object.keys(savedSettings).forEach(k => { merged[k] = savedSettings[k] })
      setRoleFilters(merged)
    } catch (e) { console.error("Failed to load role data:", e) }
    setPageLoad(false)
  }

  const handleValidSubmit = async (event, values) => {
    setLoading(true); setSubmitted(false)
    try {
      const payload = {
        ...values,
        access_level: selectedLevel,
        role_settings: JSON.stringify(roleFilters)
      }
      const resp = await api.put("update-role/" + id, payload)
      if (resp.data.success) { setSubmitted(true); setTimeout(() => setSubmitted(false), 4000) }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const handleRadioChange = async (roleId, levelName) => {
    setRoleid(roleId); setSelectedLevel(levelName); setAll(false)
    try {
      const resp = await api.get("role-settings/" + roleId)
      setRoleSets(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
      setRoleFilters(JSON.parse(resp.data.settings || "{}"))
    } catch (e) { console.error(e) }
  }

  const handleCheckboxChange = (event, moduleId, type) => {
    if (!event.target.checked) setAll(false)
    const bits = (roleFilters[moduleId] || "0000000").split("")
    bits[type] = event.target.checked ? "1" : "0"
    setRoleFilters(prev => ({ ...prev, [moduleId]: bits.join("") }))
  }

  const handleParentCheckboxChange = async (event) => {
    const checked = event.target.checked; setAll(checked)
    try {
      const resp = await api.get("role-options/" + roleid + "/" + (checked ? "checked" : "unchecked"))
      setRoleFilters(JSON.parse(resp.data.data))
    } catch (e) { console.error(e) }
  }

  // Group modules by category
  const groupedModules = roleSets.reduce((acc, mod) => {
    const cat = mod.category || "Other"
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(mod)
    return acc
  }, {})

  // Count only AVAILABLE permissions per module
  const PERM_KEYS = ["view", "add", "edit", "delete", "export", "approve", "statusupdate"]
  const getAvailableCount = (moduleId, filterStr) => {
    const mod = roleSets.find(m => m.id === moduleId)
    if (!mod) return { enabled: 0, total: 0 }
    const bits = (filterStr || "0000000").split("")
    let enabled = 0, total = 0
    PERM_KEYS.forEach((key, idx) => {
      if (mod[key] === 1) { total++; if (bits[idx] === "1") enabled++ }
    })
    return { enabled, total }
  }
  const permCount = Object.entries(roleFilters).reduce((sum, [mId, v]) => sum + getAvailableCount(parseInt(mId), v).enabled, 0)
  const totalSlots = Object.entries(roleFilters).reduce((sum, [mId, v]) => sum + getAvailableCount(parseInt(mId), v).total, 0)

  if (pageLoad) return <div className="pageLoader"><img src={PageLoader} alt="Loading..." /></div>
  if (!role) return <div className="dashBody"><p>Role not found</p></div>

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4>Edit Role</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}><Link to="/roles" className="dashBtn"><i className="fas fa-chevron-left"></i> <b>Back</b></Link></Col>
        </Row>
      </div>
      <div className="dashContent">
        <AvForm className="form-horizontal mt-0" onValidSubmit={handleValidSubmit}>
          <h6 style={{ color: "#6366f1", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px" }}>
            <i className="fas fa-user-shield"></i> Role Information
          </h6>
          <Row><Col md={12}><div className="mb-3">
            <AvField name="setting_name" label="Role Name" value={role.setting_name} type="text" required />
          </div></Col></Row>

          <h6 style={{ color: "#6366f1", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px" }}>
            <i className="fas fa-layer-group"></i> Access Level
          </h6>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "20px" }}>
            {roleAccess.map((ra, i) => {
              const isSelected = String(roleid) === String(ra.id)
              const color = LEVEL_COLORS[ra.access_level] || "#6c757d"
              return (
                <div key={i} onClick={() => handleRadioChange(ra.id, ra.access_level)}
                  style={{
                    border: isSelected ? `2px solid ${color}` : "2px solid #e2e8f0",
                    borderRadius: "10px", padding: "12px 20px", cursor: "pointer", minWidth: "120px",
                    background: isSelected ? `${color}10` : "#fff", transition: "all 0.2s ease",
                    textAlign: "center", position: "relative"
                  }}>
                  {isSelected && <i className="fas fa-check-circle" style={{ position: "absolute", top: "4px", right: "6px", color, fontSize: "14px" }}></i>}
                  <div style={{ fontWeight: "700", fontSize: "14px", color: isSelected ? color : "#1e293b" }}>{ra.access_level}</div>
                  <div style={{ fontSize: "10px", color: "#6c757d", marginTop: "2px" }}>{LEVEL_DESCRIPTIONS[ra.access_level] ? LEVEL_DESCRIPTIONS[ra.access_level].split("—")[0] : ""}</div>
                </div>
              )
            })}
          </div>
          <input type="hidden" name="access_level" value={roleid} />

          {selectedLevel && (
            <div style={{ background: `${LEVEL_COLORS[selectedLevel] || "#6c757d"}10`, border: `1px solid ${LEVEL_COLORS[selectedLevel] || "#6c757d"}30`,
              borderRadius: "8px", padding: "10px 16px", marginBottom: "16px", fontSize: "13px" }}>
              <i className="fas fa-info-circle" style={{ color: LEVEL_COLORS[selectedLevel], marginRight: "8px" }}></i>
              <strong>{selectedLevel}:</strong> {LEVEL_DESCRIPTIONS[selectedLevel] || ""}
              <span style={{ float: "right", fontSize: "12px", color: "#6c757d" }}>
                {roleSets.length} modules • {permCount}/{totalSlots} permissions enabled
              </span>
            </div>
          )}

          <div className="rolePermission" style={{ marginTop: "8px" }}>
            <div className="headeeRoles" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: "16px" }}>
                <i className="fas fa-shield-alt" style={{ marginRight: "8px" }}></i> Role Permissions
              </h2>
              {roleSets.length > 0 && (
                <label style={{ cursor: "pointer", fontWeight: "600", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <input type="checkbox" checked={all} onChange={handleParentCheckboxChange} style={{ width: "16px", height: "16px" }} />
                  Select All ({permCount}/{totalSlots})
                </label>
              )}
            </div>

            {roleid && Object.entries(groupedModules).map(([category, modules]) => (
              <div key={category} style={{ marginTop: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", padding: "6px 12px",
                  background: `${CATEGORY_COLORS[category] || "#6c757d"}10`, borderRadius: "6px", borderLeft: `3px solid ${CATEGORY_COLORS[category] || "#6c757d"}` }}>
                  <i className={`fas ${CATEGORY_ICONS[category] || "fa-folder"}`} style={{ color: CATEGORY_COLORS[category] || "#6c757d", fontSize: "13px" }}></i>
                  <strong style={{ fontSize: "13px", color: CATEGORY_COLORS[category] || "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" }}>{category}</strong>
                  <span style={{ fontSize: "11px", color: "#6c757d" }}>({modules.length})</span>
                </div>
                {modules.map((rs) => {
                  const perms = (roleFilters[rs.id] || "0000000").split("")
                  const activeCount = PERM_KEYS.filter((k, idx) => rs[k] === 1 && perms[idx] === "1").length
                  return (
                    <Row key={rs.id} style={{ padding: "7px 12px", borderBottom: "1px solid #f1f3f5", alignItems: "center",
                      background: activeCount > 0 ? "#fafffe" : "#fff" }}>
                      <Col md={4}>
                        <span style={{ fontSize: "14px", fontWeight: activeCount > 0 ? "600" : "400", color: activeCount > 0 ? "#1e293b" : "#94a3b8" }}>
                          {rs.setting_name}
                        </span>
                        {activeCount > 0 && <span className="badge bg-success ms-2" style={{ fontSize: "9px" }}>{activeCount}</span>}
                      </Col>
                      <Col md={8}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {[
                            { key: "view", label: "View", idx: 0, icon: "fa-eye", color: "#0ea5e9" },
                            { key: "add", label: "Add", idx: 1, icon: "fa-plus", color: "#22c55e" },
                            { key: "edit", label: "Edit", idx: 2, icon: "fa-edit", color: "#f59e0b" },
                            { key: "delete", label: "Delete", idx: 3, icon: "fa-trash", color: "#ef4444" },
                            { key: "export", label: "Export", idx: 4, icon: "fa-download", color: "#6366f1" },
                            { key: "approve", label: "Approve", idx: 5, icon: "fa-check-circle", color: "#10b981" },
                            { key: "statusupdate", label: "Status", idx: 6, icon: "fa-sync", color: "#8b5cf6" },
                          ].filter(p => rs[p.key] === 1).map(p => {
                            const checked = perms[p.idx] === "1"
                            return (
                              <label key={p.idx} style={{
                                cursor: "pointer", fontSize: "11px", display: "flex", alignItems: "center", gap: "3px",
                                padding: "3px 8px", borderRadius: "4px", border: `1px solid ${checked ? p.color+"40" : "#e2e8f0"}`,
                                background: checked ? p.color+"10" : "transparent", transition: "all 0.15s",
                                color: checked ? p.color : "#94a3b8", fontWeight: checked ? "600" : "400"
                              }}>
                                <input type="checkbox" checked={checked}
                                  onChange={(e) => handleCheckboxChange(e, rs.id, p.idx)}
                                  style={{ width: "13px", height: "13px", accentColor: p.color }} />
                                <i className={`fas ${p.icon}`} style={{ fontSize: "9px" }}></i>
                                {p.label}
                              </label>
                            )
                          })}
                        </div>
                      </Col>
                    </Row>
                  )
                })}
              </div>
            ))}
          </div>

          <div className="mb-3 row mt-4">
            <div className="col-6">{submitted && <UncontrolledAlert color="success"><strong>Role Updated Successfully!</strong></UncontrolledAlert>}</div>
            <div className="col-6 text-end">
              <button className="btn btn-primary w-md waves-effect waves-light" type="submit" disabled={loading}>
                {loading ? <><Spinner size="sm" /><span style={{ paddingLeft: "10px" }}>Updating...</span></> : <span><i className="fas fa-save"></i> Update Role</span>}
              </button>
            </div>
          </div>
        </AvForm>
      </div>
    </div>
  )
}

export default EditRole
